/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  Component,
  ViewChild,
  ElementRef,
  OnInit,
  OnChanges,
  ViewEncapsulation,
  Inject,
  Optional,
} from '@angular/core';
import { Observable, Subscription, forkJoin, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  filter,
  switchMap,
  tap,
} from 'rxjs/operators';
import { UntypedFormControl } from '@angular/forms';
import {
  MatLegacyDialog as MatDialog,
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
} from '@angular/material/legacy-dialog';
import { MatLegacySelect as MatSelect } from '@angular/material/legacy-select';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { MatLegacyRadioGroup as MatRadioGroup } from '@angular/material/legacy-radio';
import { RoutingService } from 'src/app/service/routing.service';
import { UserInfoService } from 'src/app/shared/services/user-info.service';
import { FeatureFlagsService } from 'src/app/shared/services/customer-api/feature-flags.service';
import { PortfolioApiService } from 'src/app/shared/services/customer-api/portfolio-api.service';
import { SearchService } from 'src/app/shared/services/metadata-api/search.service';
import { ENTER } from '@angular/cdk/keycodes';
import { MatLegacyChipInputEvent as MatChipInputEvent } from '@angular/material/legacy-chips';
import { SettingsUtilsService } from '../settings-utils.service';
import { SentimentService } from 'src/app/shared/services/data-api/sentiment.service';
import { AlertApiService } from 'src/app/shared/services/customer-api/alert-api.service';
import { FilterListService } from 'src/app/cockpit/main-menu/menu-filters/service/filter-list.service';
import { ScoreUtilsService } from 'src/app/shared/utils/score-utils.service';
import { REPORT_KIND_CONFIG } from 'src/app/micro-components/dialog-report/dialog-report.component';
import {
  AlertEventList,
  Event,
  createDefaultTrigger,
} from './alerts-setup.models';
import { ScoreAlertsComponent } from './score-alerts/score-alerts.component';
import { EventAlertsComponent } from './event-alerts/event-alerts.component';

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-alerts-setup',
  templateUrl: './alerts-setup.component.html',
  styleUrls: ['./alerts-setup.component.scss'],
})
export class AlertsSetupComponent implements OnInit, OnChanges {
  @ViewChild('selectorAlertSchedule', { static: false })
  scheduleField: MatSelect;
  @ViewChild('selectorAlertChannel', { static: false }) channelField: MatSelect;
  @ViewChild('selectorAlertType', { static: false })
  alertTypeField: MatRadioGroup;
  @ViewChild('selectorAlertObject', { static: false })
  alertObjectSelectorField: MatSelect;
  @ViewChild('inputAlertObject', { static: false, read: ElementRef })
  alertObjectInputField: ElementRef;

  @ViewChild(ScoreAlertsComponent) scoreAlerts?: ScoreAlertsComponent;
  @ViewChild(EventAlertsComponent) eventAlerts?: EventAlertsComponent;

  isButtonActive = false;

  fieldsSeen = true;

  currentAlert: any;
  alerts = [];

  notitle = false;

  scoreTypes = [];
  scoreEventsTypes = [];
  thresholdSettingTypes = [];

  selectedTabIndex = 0;
  alertTabs = [
    { label: 'Alerts', type: 'alert', value: 'alerts' },
    { label: 'Reports', type: 'report', value: 'reports' },
  ];
  reportHeaderTitle = 'New subscription';
  reportBullets: string[] = REPORT_KIND_CONFIG.score.bullets;
  selectedAlertCategory: 'scores' | 'events' = 'scores';
  expandedAlertCategories: { scores: boolean; events: boolean } = {
    scores: true,
    events: false,
  };

  searchResults: any;
  private entitySearch?: Subscription;
  folio$: Observable<any>;

  folio: any;

  loading: boolean;

  loadingAlerts: boolean;

  searchFormControl = new UntypedFormControl();

  user: any;
  domain: string;

  visible = true;
  selectable = true;
  removable = true;
  addOnBlur = true;
  readonly separatorKeysCodes: number[] = [ENTER];
  emailList = [];

  selectedDeltaPeriod = 2;
  deltaPeriods: number[] = Array.from({ length: 29 }, (_, i) => i + 2);

  /** id -> Event catalog used to decode raw event-alert `event_ids` from the API. */
  private events: Event[] = [];
  /** Set only for list-row toggle saves, where the event-alerts child may not be rendered. */
  private toggledAlertEvents: Event[] | null = null;
  private subEvents: { [id: string]: string } = {};

  constructor(
    public routing: RoutingService,
    public userInfo: UserInfoService,
    public featureFlagsService: FeatureFlagsService,
    private searchService: SearchService,
    private dialog: MatDialog,
    public snackBar: MatSnackBar,
    private settingsUtils: SettingsUtilsService,
    private sentimentService: SentimentService,
    private alertApi: AlertApiService,
    private portfolioApi: PortfolioApiService,
    private filterListService: FilterListService,
    private scoreUtils: ScoreUtilsService,
    @Optional()
    @Inject(MAT_DIALOG_DATA)
    public dialogData?: { initialTab?: string },
  ) {
    this.user = this.userInfo.user;
    this.domain = this.user.email.split('@')[1];
  }

  ngOnInit() {
    this.initializeScoreTypes();
    this.initializeSettings();
    this.initializeAlerts();
    this.initializePortfolios();
    this.applyInitialTab(this.dialogData?.initialTab);
  }

  private applyInitialTab(initialTab: string | undefined): void {
    if (initialTab === 'reports') {
      this.selectedTabIndex = 1;
      return;
    }
    this.selectedTabIndex = 0;
    if (initialTab === 'event_alerts') {
      this.selectedAlertCategory = 'events';
      this.expandedAlertCategories = { scores: false, events: true };
    } else {
      this.selectedAlertCategory = 'scores';
      this.expandedAlertCategories = { scores: true, events: false };
    }
  }

  ngOnChanges() {
    if (this.currentAlert) {
      this.updateForms();
    }
  }

  private addScoreType(
    access: boolean,
    value: string,
    label: string,
    eventLabel: string = label,
  ) {
    if (access) {
      this.scoreTypes.push({ value, label });
      this.scoreEventsTypes.push({ value, label: eventLabel });
    }
  }

  private initializeScoreTypes() {
    const settings = this.featureFlagsService.scorelabSettings;
    this.addScoreType(
      settings.sentimentAccess,
      'sentiment',
      'Sentiment',
      'All',
    );
    this.addScoreType(
      settings.generalRiskAccess,
      'general_risk',
      'General Risk Score',
    );
    this.addScoreType(
      settings.creditRiskAccess,
      'credit_risk',
      'Credit Risk Score',
    );
    this.addScoreType(settings.esgRiskAccess, 'esg_risk', 'ESG Risk Score');
    this.addScoreType(
      settings.immediateRiskAccess,
      'immediate_risk',
      'Immediate Risk Score',
    );
    this.addScoreType(settings.esgAccess, 'esg', 'ESG Score');
    if (settings.eb_simAccess) {
      this.addScoreType(true, 'eb_sim', 'EB-SIM Score');
      this.addScoreType(true, 'eb_sim_risk', 'EB-SIM Risk Score');
    }
    if (settings.bbAccess) {
      this.scoreTypes.push({ value: 'bb', label: 'Bull-Bear Score' });
    }
    if (settings.customScoreAccess && settings.customScores) {
      settings.customScores.forEach((item) => {
        this.scoreTypes.push({ value: item.uid, label: item.name });
        this.scoreEventsTypes.push({ value: item.uid, label: item.name });
      });
    }
  }

  private initializeSettings() {
    this.loadingAlerts = true;
    this.notitle = false;
    this.fieldsSeen = true;
    this.user = this.userInfo.user;
    if (this.userInfo.isAdmin) {
      this.thresholdSettingTypes.push({
        value: 'pass_through',
        label: 'Pass Through',
      });
    }
  }

  private buildEventsCatalog(eventsList: AlertEventList) {
    const categoryLabels: { [key: string]: string } = {};
    eventsList.events.forEach((el) => {
      categoryLabels[el.value] = el.label;
    });
    Object.entries(eventsList.subEvents).forEach(([key, value]) => {
      value.forEach((item) => {
        this.subEvents[item.value] = item.label;
        this.events.push(
          new Event(
            item.value,
            item.label,
            categoryLabels[key],
            key.toString(),
          ),
        );
      });
    });
  }

  private initializeAlerts() {
    this.currentAlert = this.createDefaultScoreAlert();

    forkJoin([
      this.filterListService.getEventList(),
      this.alertApi.getAlerts('score'),
      this.alertApi.getAlerts('event'),
    ]).subscribe(
      ([eventsList, scoreAlertsRes, eventAlertsRes]) => {
        this.buildEventsCatalog(eventsList);
        const resScore = scoreAlertsRes.hits.map((el) =>
          Object.assign(
            {},
            el,
            { alert_category: 'scores' },
            el.score_alert_metadata,
          ),
        );
        const resEvent = eventAlertsRes.hits.map((el) =>
          Object.assign(
            {},
            el,
            { alert_category: 'events' },
            el.event_alert_metadata,
          ),
        );
        const result = resScore.concat(resEvent);
        result.forEach((item) => {
          const alertObject = {
            alert_category: '',
            alert_type: '',
            object_type: '',
            object_id: '',
            object_name: '',
            score_type: '',
            threshold: '',
            threshold_setting: '',
            events: [],
            triggers: [],
            additional_recipients: [],
          };
          alertObject.alert_category = item.alert_category;
          alertObject.alert_type = item.portfolio_id ? 'portfolio' : 'entity';
          alertObject.object_type = item.portfolio_id
            ? 'portfolio'
            : item.entities[0].split(':')[0];
          alertObject.object_id = item.portfolio_id
            ? item.portfolio_id
            : item.entities[0].split(':')[1];
          alertObject.additional_recipients = item.additional_recipients;
          if (alertObject.alert_category === 'events') {
            // alertObject.score_type = item.score_type;
            alertObject.score_type = 'sentiment';
            alertObject.threshold = item.threshold;
            alertObject.threshold_setting = item.threshold_setting;
            const events = [...new Set(item.event_ids)];
            alertObject.events = events
              .filter((e) => this.events.filter((el) => el.id === e).length > 0)
              .map((e: any) => {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                return new Event(
                  e,
                  this.subEvents[e],
                  this.events.filter((el) => el.id === e)[0].category,
                  this.events.filter((el) => el.id === e)[0].categoryId,
                );
              });
          } else if (alertObject.alert_category === 'scores') {
            if (alertObject.alert_type === 'portfolio') {
              const triggerObject = createDefaultTrigger();
              triggerObject.score_type = item.triggers[0]['score_type'];
              if (triggerObject.score_type === 'credit_watch') {
                triggerObject.score_type = 'credit_risk';
              }
              triggerObject.threshold_setting =
                item.triggers[0].threshold_setting ===
                'z_score_unnormalized_threshold'
                  ? item.triggers[0].additional_params.operator
                  : item.triggers[0].threshold_setting;
              triggerObject.threshold =
                triggerObject.score_type === 'sentiment'
                  ? parseFloat(item.triggers[0].threshold) * 100.0
                  : item.triggers[0].threshold;
              triggerObject.isDeltaAlert =
                item.triggers[0].threshold_setting ===
                'z_score_unnormalized_threshold';
              triggerObject.additional_params.z_score_period = '7';
              triggerObject.additional_params.z_score_direction =
                item.triggers[0]['additional_params']['z_score_direction'];
              triggerObject.additional_params.z_score_threshold =
                item.triggers[0]['additional_params']['z_score_threshold'];
              triggerObject.additional_params.operator =
                item.triggers[0]['additional_params']['operator'];
              triggerObject.additional_params.plateau_period =
                item.triggers[0]['additional_params']['plateau_period'];
              alertObject.triggers.push(
                Object.assign({}, item.triggers[0], triggerObject),
              );
            } else {
              item.triggers.forEach((trigger) => {
                const triggerObject = createDefaultTrigger();
                triggerObject.score_type = trigger['score_type'];
                if (triggerObject.score_type === 'credit_watch') {
                  triggerObject.score_type = 'credit_risk';
                }
                triggerObject.threshold_setting =
                  trigger.threshold_setting === 'z_score_unnormalized_threshold'
                    ? trigger.additional_params.operator
                    : trigger.threshold_setting;
                triggerObject.threshold =
                  triggerObject.score_type === 'sentiment'
                    ? parseFloat(trigger.threshold) * 100.0
                    : trigger.threshold;
                triggerObject.isDeltaAlert =
                  trigger.threshold_setting ===
                  'z_score_unnormalized_threshold';
                triggerObject.additional_params.z_score_period = '7';
                triggerObject.additional_params.z_score_direction =
                  trigger['additional_params']['z_score_direction'];
                triggerObject.additional_params.z_score_threshold =
                  trigger['additional_params']['z_score_threshold'];
                triggerObject.additional_params.operator =
                  trigger['additional_params']['operator'];
                triggerObject.additional_params.plateau_period =
                  trigger['additional_params']['plateau_period'];
                alertObject.triggers.push(
                  Object.assign({}, trigger, triggerObject),
                );
              });
            }
          }
          this.alerts.push(Object.assign({}, item, alertObject));
        });
        if (this.alerts.length > 0) {
          const scoreAlerts = this.alerts.filter(
            (a) => a.alert_category === 'scores',
          );
          const eventAlerts = this.alerts.filter(
            (a) => a.alert_category === 'events',
          );
          if (this.selectedAlertCategory === 'events' && eventAlerts.length) {
            this.currentAlert = eventAlerts[0];
            this.updateSelectedEvents();
          } else if (scoreAlerts.length) {
            this.currentAlert = scoreAlerts[0];
          } else if (eventAlerts.length) {
            this.currentAlert = eventAlerts[0];
            this.selectedAlertCategory = 'events';
            this.expandedAlertCategories = { scores: false, events: true };
            this.updateSelectedEvents();
          }
        }
        this.updateForms();

        this.loadingAlerts = false;
      },
      () => {
        this.loadingAlerts = false;
      },
    );
  }

  private initializePortfolios() {
    this.folio$ = this.portfolioApi.portFolios();
    this.folio$.subscribe((result) => {
      this.folio = result;
    });
  }

  onTabSelection(event: number) {
    this.notitle = false;
    this.fieldsSeen = true;
    if (event === 0) {
      this.selectedTabIndex = 0;
      this.loadCurrentAlertForCategory(this.selectedAlertCategory);
    } else if (event === 1) {
      this.selectedTabIndex = 1;
    }
    this.updateForms();
  }

  onToggleSection(category: 'scores' | 'events'): void {
    this.expandedAlertCategories = {
      ...this.expandedAlertCategories,
      [category]: !this.expandedAlertCategories[category],
    };
  }

  get formTitle(): string {
    return this.selectedAlertCategory === 'events'
      ? 'Event alert information'
      : 'Score alert information';
  }

  createAlertInCategory(category: 'scores' | 'events'): void {
    this.notitle = false;
    this.fieldsSeen = false;
    this.selectedAlertCategory = category;
    this.expandedAlertCategories = {
      scores: category === 'scores',
      events: category === 'events',
    };
    if (category === 'scores') {
      this.currentAlert = this.createDefaultScoreAlert();
    } else {
      this.currentAlert = this.createDefaultEventAlert();
      this.updateSelectedEvents();
    }
    this.updateForms();
  }

  private loadCurrentAlertForCategory(category: 'scores' | 'events'): void {
    const inCategory = this.alerts.filter((a) => a.alert_category === category);
    if (inCategory.length) {
      this.currentAlert = inCategory[0];
      if (category === 'events') this.updateSelectedEvents();
    } else if (category === 'scores') {
      this.currentAlert = this.createDefaultScoreAlert();
    } else {
      this.currentAlert = this.createDefaultEventAlert();
      this.updateSelectedEvents();
    }
  }

  /**
   * Event selection state lives in EventAlertsComponent; when the child isn't
   * rendered yet (category just switched), its own init covers this call.
   * currentAlert is passed explicitly because the child's Input binding only
   * updates on the next change-detection pass.
   */
  private updateSelectedEvents() {
    this.eventAlerts?.updateSelectedEvents(this.currentAlert);
  }

  private get selectedEvents(): Event[] {
    if (this.toggledAlertEvents) {
      return this.toggledAlertEvents;
    }
    return this.eventAlerts ? this.eventAlerts.selectedEvents : [];
  }

  onFieldSeen(): void {
    this.fieldsSeen = true;
  }

  selectAlert(alert) {
    this.notitle = false;
    this.fieldsSeen = true;
    this.setCurrentAlert(JSON.parse(JSON.stringify(alert)));
    if (this.currentAlert.alert_category === 'events') {
      this.updateSelectedEvents();
    }
    this.updateForms();
  }

  private setCurrentAlert(alert: any): void {
    this.currentAlert = alert;
    if (
      alert?.alert_category === 'scores' ||
      alert?.alert_category === 'events'
    ) {
      this.selectedAlertCategory = alert.alert_category;
    }
  }

  onSlideToggle(isToggled, alert) {
    if (isToggled) {
      this.activateAlert(alert);
    } else {
      this.deactivateAlert(alert);
    }
  }

  activateAlert(alert) {
    this.notitle = false;
    this.fieldsSeen = true;
    this.setCurrentAlert(alert);
    this.saveAlert(true, true, alert.events);
  }

  deactivateAlert(alert) {
    this.notitle = false;
    this.fieldsSeen = true;
    this.setCurrentAlert(alert);
    this.saveAlert(false, false, alert.events);
  }

  createAlert() {
    this.createAlertInCategory(this.selectedAlertCategory);
  }

  private createDefaultScoreAlert(): any {
    return {
      uid: null,
      name: '',
      active: false,
      alert_type: 'entity',
      alert_category: 'scores',
      schedule: '1d',
      channel: 'email',
      layout: 'digest',
      object_type: '',
      object_id: '',
      object_name: '',
      triggers: [createDefaultTrigger()],
      additional_recipients: [],
    };
  }

  private createDefaultEventAlert(): any {
    return {
      uid: null,
      name: '',
      active: false,
      alert_type: 'entity',
      alert_category: 'events',
      schedule: '1d',
      channel: 'email',
      layout: 'digest',
      object_type: '',
      object_id: '',
      object_name: '',
      score_type: 'sentiment',
      threshold: 5,
      threshold_setting: 'z_score',
      events: [],
      additional_recipients: [],
    };
  }

  isValidAlert(alert, focus = true) {
    if (alert) {
      let isValid = true;
      if (!alert.name.trim()) {
        this.notitle = true;
        isValid = false;
      }
      if (!alert.alert_type) {
        if (this.alertTypeField) {
          // this.alertTypeField.focus();
        }
        isValid = false;
      }
      if (!alert.schedule) {
        if (this.scheduleField && focus) {
          this.scheduleField.focus();
        }
        isValid = false;
      }
      if (!alert.layout) {
        if (this.channelField && focus) {
          this.channelField.focus();
        }
        isValid = false;
      }
      if (!alert.object_type || !alert.object_id) {
        if (this.alertObjectSelectorField && focus) {
          this.alertObjectSelectorField.focus();
        }
        if (this.alertObjectInputField && focus) {
          this.alertObjectInputField.nativeElement.focus();
        }
        isValid = false;
      }
      if (alert.alert_category === 'scores') {
        alert.triggers.forEach((trigger, index) => {
          if (!trigger.score_type) {
            if (focus) {
              this.scoreAlerts?.focusTriggerScore(index);
            }
            isValid = false;
          }
          if (!trigger.threshold && !(trigger.threshold === 0)) {
            if (focus) {
              this.scoreAlerts?.focusTriggerThreshold(index);
            }
            isValid = false;
          }
          if (!trigger.threshold_setting) {
            if (focus) {
              this.scoreAlerts?.focusTriggerThresholdSetting(index);
            }
            isValid = false;
          }
          if (trigger.isDeltaAlert) {
            if (!trigger.additional_params.z_score_period) {
              isValid = false;
            }
            if (!trigger.additional_params.z_score_direction) {
              isValid = false;
            }
            if (!trigger.additional_params.z_score_threshold) {
              isValid = false;
            }
            if (!trigger.additional_params.plateau_period) {
              isValid = false;
            }
          }
        });
      } else if (alert.alert_category === 'events') {
        if (!alert.score_type) {
          if (focus) {
            this.eventAlerts?.focusScoreType();
          }
          isValid = false;
        }
        if (!(this.selectedEvents && this.selectedEvents.length > 0)) {
          if (focus) {
            this.eventAlerts?.focusEventsInput();
          }
          isValid = false;
        }
        if (!alert.threshold && !(alert.threshold === 0)) {
          isValid = false;
        }
        if (!alert.threshold_setting) {
          isValid = false;
        }
      }
      return isValid;
    } else {
      return false;
    }
  }

  // toggledAlertEvents: list-row toggle saves pass the toggled alert's own
  // events, since the event-alerts child may not be rendered (or may hold a
  // different alert's selection) at that point.
  saveAlert(isActive, isActivated, toggledAlertEvents?: Event[]) {
    this.toggledAlertEvents = toggledAlertEvents ?? null;
    try {
      this.notitle = false;
      this.currentAlert.additional_recipients = this.emailList;
      if (
        this.isValidAlert(this.currentAlert) &&
        ((this.fieldsSeen && this.selectedAlertCategory === 'events') ||
          this.selectedAlertCategory === 'scores')
      ) {
        this.currentAlert.active = isActive;
        const payloadObject = {};
        const payloadObjectMetadata = {};
        let isNew;
        if (this.currentAlert.uid === null) {
          isNew = true;
        } else {
          isNew = false;
        }
        this.createMainPayloadProperties(payloadObject);
        this.createThresholdPayloadProperties(payloadObjectMetadata);
        if (this.currentAlert.alert_category === 'scores') {
          payloadObject['score_alert_metadata'] = payloadObjectMetadata;
        }
        if (this.currentAlert.alert_category === 'events') {
          payloadObject['event_alert_metadata'] = payloadObjectMetadata;
        }
        this.storeAlert(payloadObject, isNew, isActivated);
        this.fieldsSeen = true;
      } else {
        if (
          (this.fieldsSeen && this.selectedAlertCategory === 'events') ||
          this.selectedAlertCategory === 'scores'
        ) {
          alert('Error: all of the required fields must be filled in.');
        }
      }
    } finally {
      this.toggledAlertEvents = null;
    }
  }

  private createMainPayloadProperties(payloadObject) {
    if (this.currentAlert.uid) {
      payloadObject['uid'] = this.currentAlert.uid;
    }
    payloadObject['active'] = this.currentAlert.active;
    payloadObject['schedule'] = this.currentAlert.schedule;
    payloadObject['channel'] = 'email';
    payloadObject['layout'] = this.currentAlert.layout;
    payloadObject['name'] = this.currentAlert.name;
    payloadObject['additional_recipients'] =
      this.currentAlert.additional_recipients;
    if (this.currentAlert.alert_category === 'events') {
      this.currentAlert.events = this.selectedEvents;
    }
  }

  private createThresholdPayloadProperties(payloadObjectMetadata) {
    if (this.currentAlert.alert_type) {
      if (this.currentAlert.alert_type === 'entity') {
        payloadObjectMetadata['portfolio_id'] = null;
        payloadObjectMetadata['entities'] = [
          this.currentAlert.object_type + ':' + this.currentAlert.object_id,
        ];
      } else if (this.currentAlert.alert_type === 'portfolio') {
        payloadObjectMetadata['portfolio_id'] = this.currentAlert.object_id;
        payloadObjectMetadata['entities'] = [];
      }
      if (this.currentAlert.alert_type === 'portfolio') {
        if (this.currentAlert.alert_category === 'scores') {
          payloadObjectMetadata['triggers'] = [];
          const payloadObjectTrigger = {};
          if (this.currentAlert.triggers[0].score_type === 'sentiment') {
            payloadObjectTrigger['threshold'] =
              this.currentAlert.triggers[0].threshold / 100.0;
          } else {
            payloadObjectTrigger['threshold'] =
              this.currentAlert.triggers[0].threshold;
          }
          payloadObjectTrigger['threshold_setting'] = this.currentAlert
            .triggers[0].isDeltaAlert
            ? 'z_score_unnormalized_threshold'
            : this.currentAlert.triggers[0].threshold_setting;
          payloadObjectTrigger['score_type'] =
            this.currentAlert.triggers[0].score_type;
          payloadObjectTrigger['additional_params'] = {};
          payloadObjectTrigger['additional_params']['z_score_period'] =
            this.currentAlert.triggers[0].additional_params.z_score_period;
          payloadObjectTrigger['additional_params']['z_score_direction'] =
            this.currentAlert.triggers[0].additional_params.z_score_direction;
          payloadObjectTrigger['additional_params']['z_score_threshold'] =
            this.currentAlert.triggers[0].additional_params.z_score_threshold +
            '';
          payloadObjectTrigger['additional_params']['operator'] =
            this.currentAlert.triggers[0].threshold_setting;
          payloadObjectTrigger['additional_params']['plateau_period'] =
            this.currentAlert.triggers[0].additional_params.plateau_period;
          if (this.currentAlert.triggers[0].score_type === 'general_risk') {
            payloadObjectTrigger['additional_params'].with_black_swan = 'true';
          } else if (this.currentAlert.triggers[0].score_type === 'esg_risk') {
            payloadObjectTrigger['additional_params'].with_black_swan = 'true';
          } else if (
            this.currentAlert.triggers[0].score_type === 'immediate_risk'
          ) {
            payloadObjectTrigger['additional_params'].with_black_swan = 'true';
          } else if (
            this.currentAlert.triggers[0].score_type === 'credit_risk'
          ) {
            payloadObjectTrigger['additional_params'].with_black_swan = 'true';
            payloadObjectTrigger['additional_params'].risk_sensitivity = '0';
            payloadObjectTrigger['score_type'] = 'credit_watch';
          } else if (this.currentAlert.triggers[0].score_type === 'esg') {
            payloadObjectTrigger[
              'additional_params'
            ].disable_industry_materiality = 'true';
          } else if (this.currentAlert.triggers[0].score_type === 'bb') {
            payloadObjectTrigger['additional_params'].time_horizon = '1M';
          } else if (this.currentAlert.triggers[0].score_type) {
            Object.assign(
              payloadObjectTrigger['additional_params'],
              this.scoreUtils.getCustomScoreAlertParams(
                this.currentAlert.triggers[0].score_type,
              ),
            );
          }
          payloadObjectMetadata['triggers'].push(payloadObjectTrigger);
        } else if (this.currentAlert.alert_category === 'events') {
          payloadObjectMetadata['event_ids'] = this.currentAlert.events.map(
            (e) => e.id,
          );
          payloadObjectMetadata['threshold'] = this.currentAlert.threshold;
          payloadObjectMetadata['threshold_setting'] =
            this.currentAlert.threshold_setting;
          if (this.currentAlert.threshold === 0) {
            payloadObjectMetadata['threshold_setting'] = 'pass_through';
          }
          payloadObjectMetadata['additional_params'] = {};
        }
      } else {
        if (this.currentAlert.alert_category === 'scores') {
          payloadObjectMetadata['triggers'] = [];
          this.currentAlert.triggers.forEach((trigger) => {
            const payloadObjectTrigger = {};
            payloadObjectTrigger['score_type'] = trigger.score_type;
            if (trigger.score_type === 'sentiment') {
              payloadObjectTrigger['threshold'] = trigger.threshold / 100.0;
            } else {
              payloadObjectTrigger['threshold'] = trigger.threshold;
            }
            payloadObjectTrigger['threshold_setting'] =
              trigger.threshold_setting;
            payloadObjectTrigger['threshold_setting'] = trigger.isDeltaAlert
              ? 'z_score_unnormalized_threshold'
              : trigger.threshold_setting;
            payloadObjectTrigger['additional_params'] = {};
            payloadObjectTrigger['additional_params']['z_score_period'] =
              trigger.additional_params.z_score_period;
            payloadObjectTrigger['additional_params']['z_score_direction'] =
              trigger.additional_params.z_score_direction;
            payloadObjectTrigger['additional_params']['z_score_threshold'] =
              trigger.additional_params.z_score_threshold + '';
            payloadObjectTrigger['additional_params']['operator'] =
              trigger.threshold_setting;
            payloadObjectTrigger['additional_params']['plateau_period'] =
              trigger.additional_params.plateau_period;
            if (trigger.score_type === 'general_risk') {
              payloadObjectTrigger['additional_params'].with_black_swan =
                'true';
            } else if (trigger.score_type === 'esg_risk') {
              payloadObjectTrigger['additional_params'].with_black_swan =
                'true';
            } else if (trigger.score_type === 'immediate_risk') {
              payloadObjectTrigger['additional_params'].with_black_swan =
                'true';
            } else if (trigger.score_type === 'credit_risk') {
              payloadObjectTrigger['additional_params'].with_black_swan =
                'true';
              payloadObjectTrigger['additional_params'].risk_sensitivity = '0';
              payloadObjectTrigger['score_type'] = 'credit_watch';
            } else if (trigger.score_type === 'esg') {
              payloadObjectTrigger[
                'additional_params'
              ].disable_industry_materiality = 'true';
            } else if (trigger.score_type === 'bb') {
              payloadObjectTrigger['additional_params'].time_horizon = '1M';
            } else if (trigger.score_type) {
              Object.assign(
                payloadObjectTrigger['additional_params'],
                this.scoreUtils.getCustomScoreAlertParams(trigger.score_type),
              );
            }
            payloadObjectMetadata['triggers'].push(payloadObjectTrigger);
          });
        } else if (this.currentAlert.alert_category === 'events') {
          payloadObjectMetadata['event_ids'] = this.currentAlert.events.map(
            (e) => e.id,
          );
          payloadObjectMetadata['threshold'] = this.currentAlert.threshold;
          payloadObjectMetadata['threshold_setting'] =
            this.currentAlert.threshold_setting;
          if (this.currentAlert.threshold === 0) {
            payloadObjectMetadata['threshold_setting'] = 'pass_through';
          }
          payloadObjectMetadata['additional_params'] = {};
        }
      }
    }
  }

  private storeAlert(payloadObject, isNew, isActivated) {
    this.alertApi.addAlert(payloadObject).subscribe(
      (result) => {
        this.currentAlert.uid = result.uid;
        if (isNew) {
          this.alerts.push(this.currentAlert);
        }
        const alertsCopy = JSON.parse(JSON.stringify(this.alerts));
        if (!isNew) {
          alertsCopy.forEach((a, index) => {
            if (a.uid === this.currentAlert.uid) {
              alertsCopy[index] = JSON.parse(JSON.stringify(this.currentAlert));
            }
          });
        }
        this.alerts = [];
        this.alerts = alertsCopy;
        let alertMessage = 'saved';
        if (isActivated === true) {
          alertMessage = 'activated';
        } else if (isActivated === false) {
          alertMessage = 'deactivated';
        }
        this.snackBar.open('Alert ' + alertMessage + '.', 'OK', {
          duration: 5000,
        });
      },
      () => {
        this.snackBar.open(
          'Saving the Event Alert was not possible. Please reduce the number of Events.',
          'OK',
          { duration: 5000 },
        );
      },
    );
  }

  deleteAlert() {
    if (this.currentAlert.uid) {
      if (window.confirm('Delete ' + this.currentAlert.name + ' alert?')) {
        this.alertApi.removeAlert(this.currentAlert.uid).subscribe();
        this.alerts = this.alerts.filter((item) => {
          return item.uid !== this.currentAlert.uid;
        });
        this.onTabSelection(this.selectedTabIndex);
        this.snackBar.open('Alert deleted.', 'OK', { duration: 5000 });
      }
    }
  }

  iClose() {
    this.dialog.closeAll();
  }

  optSelect(option) {
    this.searchFormControl.setValue(option.entity.name, { emitEvent: false });
    this.currentAlert.object_type = option.entity.type;
    this.currentAlert.object_id = option.entity.alpha_id;
    this.currentAlert.object_name = option.entity.name;
  }

  optSelect2() {
    this.currentAlert.object_type = '';
    this.currentAlert.object_id = '';
    this.currentAlert.object_name = '';
    this.currentAlert.triggers = [createDefaultTrigger()];
    this.searchFormControl.setValue('');
  }

  optSelect3(event, type) {
    let name = '';
    if (type === 'portfolio') {
      name = this.folio.filter((element) => {
        return element.uid === event.value;
      })[0].name;
      this.currentAlert.object_type = 'portfolio';
    }
    this.currentAlert.object_name = name;
  }

  updateForms() {
    this.emailList = this.currentAlert.additional_recipients;

    this.searchFormControl = new UntypedFormControl();

    let name = '';
    this.searchFormControl.setValue('');

    if (this.currentAlert.object_type && this.currentAlert.object_id) {
      if (this.currentAlert.alert_type === 'entity') {
        this.sentimentService
          .getSentiment(
            {
              type: this.currentAlert.object_type,
              id: this.currentAlert.object_id,
              fromAlertsSetup: true,
            },
            false,
          )
          .subscribe((result) => {
            this.searchFormControl.setValue(result.entity.name, {
              emitEvent: false,
            });
            this.currentAlert.object_name = result.entity.name;
          });
      } else if (this.currentAlert.alert_type === 'portfolio') {
        name = this.folio
          ? this.folio.filter((element) => {
              return element.uid === this.currentAlert.object_id;
            })[0].name
          : '';
        this.currentAlert.object_name = name;
      }
    }

    // Subscribed here, not via `async` in the panel: the panel only renders
    // while open, so a search started with it closed never cleared `loading`.
    this.entitySearch?.unsubscribe();
    this.loading = false;
    this.searchResults = null;
    this.entitySearch = this.searchFormControl.valueChanges
      .pipe(
        debounceTime(1000),
        filter((value) => value && value.length > 1),
        tap(() => (this.loading = true)),
        switchMap((value) =>
          this.searchService
            .searchWithFallback(value)
            .pipe(catchError(() => of(null))),
        ),
      )
      .subscribe((result) => {
        this.searchResults = result;
        this.loading = false;
      });
  }

  onChannelChange() {
    if (this.currentAlert.channel === 'digest') {
      /* empty */
    } else if (this.currentAlert.channel === 'explainable_digest') {
      this.currentAlert.triggers = [createDefaultTrigger()];
    }
  }

  elementMatches(options, value) {
    const filterValue = value.toLowerCase();
    return options.filter(
      (item) => item.name.toLowerCase().indexOf(filterValue) === 0,
    );
  }

  displayFn(element) {
    return element && element.name ? element.name : '';
  }

  /**
   *
   */
  iMatch(entity) {
    return this.settingsUtils.iMatch(entity);
  }

  /**
   * format big numbers >999 into K, M, ...
   */
  numberFormat(num) {
    return this.settingsUtils.numberFormat(num);
  }

  add(event: MatChipInputEvent): void {
    const { updatedList, error } = this.settingsUtils.handleEmailAdd(
      event.value,
      this.emailList,
      this.user.email,
      this.domain,
    );
    if (error) {
      alert(error);
    } else {
      this.emailList = updatedList;
    }
    this.currentAlert.additional_recipients = this.emailList;
    if (event.input) {
      event.input.value = '';
    }
  }

  remove(email): void {
    this.emailList = this.settingsUtils.removeEmail(this.emailList, email);
  }

  getCreateAlertId(): string {
    return `create-${this.selectedAlertCategory}-alert`;
  }
}
